/**
 * Share-link API client.
 *
 * Endpoints :
 *   POST   /api/share/create
 *   GET    /api/share/{token}      ← public, no auth required
 *   POST   /api/share/{token}/respond  ← public
 *   GET    /api/share/my-links/list
 *   DELETE /api/share/{token}
 */
import { API_URL, messageDErreur } from './client';
import { useAuthStore } from '../store/useAuthStore';

/** Helper : appelle l'API avec auth JWT + auto-refresh sur 401. */
async function authFetch(path, options = {}) {
  const { authFetch: af } = useAuthStore.getState();
  const res = await af(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    body: options.body && typeof options.body === 'object' ? JSON.stringify(options.body) : options.body,
  });
  if (!res.ok) {
    let detail = `Erreur ${res.status}`;
    try { const j = await res.json(); if (j.detail) detail = messageDErreur(j.detail, detail); } catch (_) { /* not json */ }
    throw new Error(detail);
  }
  return res.json();
}

/** Crée un nouveau share link à partir du projet courant. */
export async function createShare({ projectData, title = null, expiryDays = 7 }) {
  return authFetch('/api/share/create', {
    method: 'POST',
    body: {
      project_data: projectData,
      title,
      expiry_days: expiryDays,
    },
  });
}

/** Récupère le snapshot d'un share link (public, no auth). */
export async function getShareByToken(token) {
  const res = await fetch(`${API_URL}/api/share/${encodeURIComponent(token)}`);
  if (!res.ok) {
    let detail = `Erreur ${res.status}`;
    try { const j = await res.json(); if (j.detail) detail = messageDErreur(j.detail, detail); } catch (_) { /* not json */ }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/** Envoie la réponse du client (approve ou request_changes). Public. */
export async function respondToShare(token, action, feedback = '') {
  const res = await fetch(`${API_URL}/api/share/${encodeURIComponent(token)}/respond`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, feedback }),
  });
  if (!res.ok) {
    let detail = `Erreur ${res.status}`;
    try { const j = await res.json(); if (j.detail) detail = messageDErreur(j.detail, detail); } catch (_) { /* not json */ }
    throw new Error(detail);
  }
  return res.json();
}

/** Liste les share links créés par l'utilisateur courant. */
export async function listMyShares() {
  return authFetch('/api/share/my-links/list');
}

/** Révoque (supprime) un share link. */
export async function revokeShare(token) {
  return authFetch(`/api/share/${encodeURIComponent(token)}`, { method: 'DELETE' });
}
